import * as React from "react";
import clsx from "clsx";
import DotPulseLdr from "@/components/ui/DotPulseLdr";
import type { TagOption } from "@/cms/editor/TagPickerPanel";

const filterTags = (tags: TagOption[], query: string) => {
  const q = query.trim().toLowerCase();
  if (!q) return tags;
  return tags.filter((tag) => {
    const fields = [
      tag.name_en,
      tag.name_fa ?? "",
      tag.slug_en ?? "",
      tag.slug_fa ?? "",
    ]
      .filter(Boolean)
      .map((v) => v.toLowerCase());
    return fields.some((v) => v.includes(q));
  });
};

export type TagManagerListProps = {
  selectedTags: TagOption[];
  tagManagerDisabled: boolean;
  tagFilter: string;
  onTagFilterChange: (value: string) => void;
  onClearFilter: () => void;
  onRemoveTag: (tagId: string) => void;
  tagsLoading: boolean;
  tagsError: string | null;
  allTags: TagOption[];
  onAddTag: (tag: TagOption) => void;
  listClassName?: string;
};

export function TagManagerList({
  selectedTags,
  tagManagerDisabled,
  tagFilter,
  onTagFilterChange,
  onClearFilter,
  onRemoveTag,
  tagsLoading,
  tagsError,
  allTags,
  onAddTag,
  listClassName,
}: TagManagerListProps) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {selectedTags.length === 0 ? (
          <span className="text-sm text-slate-500">No tags assigned.</span>
        ) : (
          selectedTags.map((tag) => (
            <span
              key={tag.id}
              className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700"
            >
              {tag.name_en}
              <button
                type="button"
                onClick={() => onRemoveTag(tag.id)}
                className="rounded-full bg-white p-1 text-slate-500 hover:bg-gray-200 disabled:opacity-50"
                disabled={tagManagerDisabled}
                aria-label={`Remove tag ${tag.name_en}`}
              >
                x
              </button>
            </span>
          ))
        )}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={tagFilter}
          onChange={(event) => onTagFilterChange(event.target.value)}
          placeholder="Search tags..."
          className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-none transition focus:border-pinkCustom"
          disabled={tagManagerDisabled}
        />
        <button
          type="button"
          className="rounded-md border border-gray-200 px-3 py-2 text-sm text-slate-600 hover:bg-gray-100"
          onClick={onClearFilter}
          disabled={tagManagerDisabled || tagFilter.length === 0}
        >
          Clear
        </button>
      </div>

      <div className={clsx("min-h-[180px] rounded-md border border-gray-200", listClassName)}>
        {tagsLoading ? (
          <div className="flex h-40 items-center justify-center">
            <DotPulseLdr color="var(--color-pinkCustom)" />
          </div>
        ) : tagsError ? (
          <div className="p-3 text-sm text-rose-600">{tagsError}</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {filterTags(allTags, tagFilter)
              .filter((tag) => !selectedTags.some((t) => t.id === tag.id))
              .map((tag) => (
                <li key={tag.id}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-pinkCustom/5 disabled:opacity-60"
                    onClick={() => onAddTag(tag)}
                    disabled={tagManagerDisabled}
                  >
                    <div className="space-y-0.5">
                      <p className="font-semibold text-slate-800">
                        {tag.name_en}
                      </p>
                      {tag.name_fa ? (
                        <p className="text-xs text-slate-500" dir="rtl">
                          {tag.name_fa}
                        </p>
                      ) : null}
                    </div>
                    <span className="text-[11px] font-semibold uppercase text-pinkCustom">
                      Add
                    </span>
                  </button>
                </li>
              ))}
          </ul>
        )}
      </div>
    </div>
  );
}
